import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendarAlt, faDollarSign, faFolderOpen, faPlus, faTimes, faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import { calculateContractDuration, calculateOptionPeriod, formatPeriod, parsePeriodValue } from '../../utils/periodUtils';

function ContractForm({ initialData, onSave, onCancel, isSaving }) {
  // Form fields
  const [discipline, setDiscipline] = useState('');
  const [referenceNo, setReferenceNo] = useState('');
  const [vendors, setVendors] = useState([]);
  const [vendorInput, setVendorInput] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  
  const [duration, setDuration] = useState({ years: 0, months: 0, weeks: 0, totalMonths: 0, totalWeeks: 0 }); // Total Duration (Calculated)
  const [basePeriodMonths, setBasePeriodMonths] = useState(''); // Base Contract Period Months
  const [basePeriodWeeks, setBasePeriodWeeks] = useState(''); // Base Contract Period Weeks
  const [baseAmount, setBaseAmount] = useState(''); // Base Contract Amount ($)
  
  const [optionPeriod, setOptionPeriod] = useState({ years: 0, months: 0, weeks: 0, totalWeeks: 0 }); // Option Period (Calculated)
  const [optionAmount, setOptionAmount] = useState(''); // Option Amount ($)
  
  const [contingencyPercentage, setContingencyPercentage] = useState(5); // Defaults to 5%
  const [contingencyAmount, setContingencyAmount] = useState(0); // Contingency Amount (Calculated)
  
  // Validation errors state
  const [errors, setErrors] = useState({});

  // Helper to clear specific error when user updates input
  const clearError = (fieldName) => {
    if (errors[fieldName]) {
      setErrors((prev) => {
        const copy = { ...prev };
        delete copy[fieldName];
        return copy;
      });
    }
  };

  const hasOptionPeriod = Boolean(
    optionPeriod.totalWeeks > 0 ||
    optionPeriod.years > 0 ||
    optionPeriod.months > 0 ||
    optionPeriod.weeks > 0
  );

  // Populate form if editing
  useEffect(() => {
    if (initialData) {
      setDiscipline(initialData.discipline || '');
      setReferenceNo(initialData.referenceNo || '');
      setVendors(initialData.vendors || []);
      setStartDate(initialData.startDate || '');
      setEndDate(initialData.endDate || '');
      
      const parsedBase = parsePeriodValue(
        initialData.basePeriodYears !== undefined 
          ? { years: initialData.basePeriodYears, months: initialData.basePeriodMonths, weeks: initialData.basePeriodWeeks }
          : initialData.basePeriod
      );
      setBasePeriodMonths(parsedBase.totalMonths > 0 ? String(parsedBase.totalMonths) : (parsedBase.totalWeeks === 0 ? '' : '0'));
      setBasePeriodWeeks(parsedBase.weeks > 0 ? String(parsedBase.weeks) : (parsedBase.totalWeeks === 0 ? '' : '0'));

      setBaseAmount(initialData.baseAmount !== undefined ? String(initialData.baseAmount) : '');
      setOptionAmount(initialData.optionAmount !== undefined ? String(initialData.optionAmount) : '');
      setContingencyPercentage(initialData.contingencyPercentage !== undefined ? initialData.contingencyPercentage : 5);
    }
  }, [initialData]);

  // Recalculate duration when dates change
  useEffect(() => {
    if (startDate && endDate) {
      const dur = calculateContractDuration(startDate, endDate);
      setDuration(dur);

      // Auto-adjust base period if it exceeds new duration
      const currentBaseWeeks = (parseInt(basePeriodMonths) || 0) * 4 + (parseInt(basePeriodWeeks) || 0);
      if (dur.totalWeeks > 0 && currentBaseWeeks > dur.totalWeeks) {
        setBasePeriodMonths(String(dur.totalMonths || dur.months));
        setBasePeriodWeeks(String(dur.weeks));
      }
    } else {
      setDuration({ years: 0, months: 0, weeks: 0, totalMonths: 0, totalWeeks: 0 });
    }
  }, [startDate, endDate]);

  // Recalculate option period when basePeriod or duration changes
  useEffect(() => {
    const durTotalWeeks = duration.totalWeeks || 0;
    const baseMonthsNum = parseInt(basePeriodMonths) || 0;
    const baseWeeksNum = parseInt(basePeriodWeeks) || 0;
    const baseTotalWeeks = baseMonthsNum * 4 + baseWeeksNum;

    if (durTotalWeeks > 0 && baseTotalWeeks > durTotalWeeks) {
      setOptionPeriod({ years: 0, months: 0, weeks: 0, totalWeeks: 0 });
      setErrors(prev => ({ 
        ...prev, 
        basePeriod: `Cannot exceed total duration (${formatPeriod(0, duration.totalMonths || duration.months, duration.weeks)})` 
      }));
    } else {
      setErrors(prev => {
        const copy = { ...prev };
        delete copy.basePeriod;
        return copy;
      });
      const opt = calculateOptionPeriod(duration, { years: 0, months: baseMonthsNum, weeks: baseWeeksNum });
      setOptionPeriod(opt);
    }
  }, [basePeriodMonths, basePeriodWeeks, duration]);

  // Recalculate contingency amount
  useEffect(() => {
    const baseAmt = parseFloat(baseAmount) || 0;
    const optAmt = parseFloat(optionAmount) || 0;
    const pct = parseFloat(contingencyPercentage) || 0;
    
    const contingencyAmt = (baseAmt + optAmt) * (pct / 100);
    setContingencyAmount(parseFloat(contingencyAmt.toFixed(2)));
  }, [baseAmount, optionAmount, contingencyPercentage]);

  // Add vendor tag
  const handleAddVendor = (e) => {
    e.preventDefault();
    const val = vendorInput.trim();
    if (val && !vendors.includes(val)) {
      setVendors([...vendors, val]);
      setVendorInput('');
    }
  };

  // Remove vendor tag
  const handleRemoveVendor = (indexToRemove) => {
    setVendors(vendors.filter((_, idx) => idx !== indexToRemove));
  };

  // Calculations for summary card
  const basePlusOption = (parseFloat(baseAmount) || 0) + (parseFloat(optionAmount) || 0);
  const totalApvValue = basePlusOption + contingencyAmount;

  // Format helper
  const formatValue = (num) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(num);
  };

  // Form submit handler
  const handleSubmit = (e) => {
    e.preventDefault();
    const newErrors = {};

    // 1. Discipline *
    if (!discipline || !discipline.trim()) {
      newErrors.discipline = 'Discipline is required';
    }

    // 2. APV Tender Reference No *
    if (!referenceNo || !referenceNo.trim()) {
      newErrors.referenceNo = 'APV Tender Reference No is required';
    }

    // 3. Start Date *
    if (!startDate) {
      newErrors.startDate = 'Start Date is required';
    } else if (isNaN(new Date(startDate).getTime())) {
      newErrors.startDate = 'Start Date must be a valid date';
    }

    // 4. End Date *
    if (!endDate) {
      newErrors.endDate = 'End Date is required';
    } else if (isNaN(new Date(endDate).getTime())) {
      newErrors.endDate = 'End Date must be a valid date';
    } else if (startDate && !isNaN(new Date(startDate).getTime()) && new Date(startDate) > new Date(endDate)) {
      newErrors.endDate = 'End Date must be on or after Start Date';
    }

    // 5. Base Contract Period *
    const baseMonthsNum = parseInt(basePeriodMonths, 10) || 0;
    const baseWeeksNum = parseInt(basePeriodWeeks, 10) || 0;
    const baseTotalWeeks = baseMonthsNum * 4 + baseWeeksNum;

    if (baseTotalWeeks <= 0) {
      newErrors.basePeriod = 'Base Contract Period is required and must be greater than 0';
    } else if (duration.totalWeeks > 0 && baseTotalWeeks > duration.totalWeeks) {
      newErrors.basePeriod = `Base Contract Period cannot exceed total duration (${formatPeriod(0, duration.totalMonths || duration.months, duration.weeks)})`;
    }

    // 6. Base Contract Amount ($) *
    if (baseAmount === '' || baseAmount === null || baseAmount === undefined || isNaN(Number(baseAmount))) {
      newErrors.baseAmount = 'Base Contract Amount is required and must be a valid number';
    } else if (Number(baseAmount) < 0) {
      newErrors.baseAmount = 'Base Contract Amount cannot be negative';
    }

    // 7. Option Amount ($) * (Required when Option Period exists)
    if (hasOptionPeriod) {
      if (optionAmount === '' || optionAmount === null || optionAmount === undefined || isNaN(Number(optionAmount))) {
        newErrors.optionAmount = 'Option Amount is required when Option Period exists';
      } else if (Number(optionAmount) < 0) {
        newErrors.optionAmount = 'Option Amount cannot be negative';
      }
    } else if (optionAmount !== '' && optionAmount !== null && optionAmount !== undefined) {
      if (isNaN(Number(optionAmount)) || Number(optionAmount) < 0) {
        newErrors.optionAmount = 'Option Amount must be 0 or greater';
      }
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      // Scroll to top of scrollable form body
      const formBody = document.querySelector('.apv-form-scrollable-content');
      if (formBody) {
        formBody.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        const formCard = document.querySelector('.apv-form-card');
        if (formCard) formCard.scrollIntoView({ behavior: 'smooth' });
      }
      return;
    }

    const formattedDuration = formatPeriod(0, duration.totalMonths || duration.months, duration.weeks);
    const formattedBasePeriod = formatPeriod(0, baseMonthsNum, baseWeeksNum);
    const formattedOptionPeriod = formatPeriod(0, optionPeriod.months, optionPeriod.weeks);

    // Save callback
    onSave({
      id: initialData ? initialData.id : `apv-${Date.now()}`,
      discipline,
      referenceNo,
      vendors,
      startDate,
      endDate,
      duration: formattedDuration,
      durationYears: 0,
      durationMonths: duration.totalMonths || duration.months,
      durationWeeks: duration.weeks,
      durationTotalMonths: duration.totalMonths || duration.months,
      durationTotalWeeks: duration.totalWeeks,
      basePeriod: formattedBasePeriod,
      basePeriodYears: 0,
      basePeriodMonths: baseMonthsNum,
      basePeriodWeeks: baseWeeksNum,
      basePeriodTotalMonths: baseMonthsNum,
      basePeriodTotalWeeks: baseTotalWeeks,
      baseAmount: parseFloat(baseAmount),
      optionPeriod: formattedOptionPeriod,
      optionPeriodYears: 0,
      optionPeriodMonths: optionPeriod.months,
      optionPeriodWeeks: optionPeriod.weeks,
      optionPeriodTotalMonths: optionPeriod.months,
      optionPeriodTotalWeeks: optionPeriod.totalWeeks,
      optionAmount: parseFloat(optionAmount || 0),
      contingencyPercentage: parseFloat(contingencyPercentage),
      contingencyAmount,
      totalApv: totalApvValue,
      remarks: e.target.remarks.value || ''
    });
  };

  return (
    <div className="apv-container apv-form-view">
      <div className="apv-form-card">
        {/* Sticky Header Section: Back button & Title banner */}
        <div className="apv-form-sticky-header">
          {/* Back button bar */}
          <div className="apv-form-top-bar">
            <button 
              type="button"
              onClick={onCancel}
              className="apv-back-btn"
            >
              <FontAwesomeIcon icon={faArrowLeft} /> Back to List
            </button>
          </div>

          {/* Form header banner */}
          <div className="apv-form-header-band">
            <h3>{initialData ? 'Edit Contract' : 'New Contract'}</h3>
            <p>Contract Management Form - Enter contract details and calculate APV amounts</p>
          </div>
        </div>

        {/* Scrollable Form Content */}
        <form onSubmit={handleSubmit} className="apv-form-body apv-form-scrollable-content">
          {Object.keys(errors).length > 0 && (
            <div className="apv-form-error-banner" role="alert">
              <span><strong>Please fix the errors below:</strong> All fields marked with <span className="apv-required-star">*</span> are required.</span>
            </div>
          )}

          <div className="apv-form-grid">
            
            {/* Left section: general info */}
            <div className="apv-form-section">
              <div className="apv-form-section-title">
                <FontAwesomeIcon icon={faFolderOpen} className="text-primary" /> General Contract Info
              </div>
              
              <div className="apv-form-group">
                <label>Discipline <span className="apv-required-star">*</span></label>
                <select 
                  value={discipline} 
                  onChange={(e) => {
                    setDiscipline(e.target.value);
                    clearError('discipline');
                  }}
                  className={errors.discipline ? 'invalid' : ''}
                >
                  <option value="">Select Discipline</option>
                  <option value="M&E (Mechanical & Electrical)">M&E (Mechanical & Electrical)</option>
                  <option value="Architecture">Architecture</option>
                  <option value="Quantity Surveying">Quantity Surveying</option>
                  <option value="Civil & Structural">Civil & Structural</option>
                  <option value="Contractor">Contractor</option>
                </select>
                {errors.discipline && <span className="apv-error-message">{errors.discipline}</span>}
              </div>

              <div className="apv-form-group">
                <label>APV Tender Reference No <span className="apv-required-star">*</span></label>
                <input 
                  type="text" 
                  placeholder="Enter Ariba reference number (e.g. APV-2024-001)"
                  value={referenceNo}
                  onChange={(e) => {
                    setReferenceNo(e.target.value);
                    clearError('referenceNo');
                  }}
                  className={errors.referenceNo ? 'invalid' : ''}
                />
                {errors.referenceNo && <span className="apv-error-message">{errors.referenceNo}</span>}
              </div>

              <div className="apv-form-group">
                <label>Vendors (Add Multiple Vendor Names)</label>
                <div className="apv-vendor-input-wrap">
                  <input 
                    type="text" 
                    placeholder="Enter vendor name..." 
                    value={vendorInput}
                    onChange={(e) => setVendorInput(e.target.value)}
                    onKeyPress={(e) => { if (e.key === 'Enter') handleAddVendor(e); }}
                  />
                  <button type="button" onClick={handleAddVendor}>Add</button>
                </div>
                <div className="apv-form-vendors-display">
                  {vendors.length > 0 ? (
                    vendors.map((vendor, index) => (
                      <span key={index} className="vendor-tag">
                        {vendor}
                        <span className="vendor-tag-delete" onClick={() => handleRemoveVendor(index)}>
                          <FontAwesomeIcon icon={faTimes} />
                        </span>
                      </span>
                    ))
                  ) : (
                    <span style={{ color: '#adb5bd', fontSize: '0.8rem' }}>No vendors added yet.</span>
                  )}
                </div>
              </div>
            </div>

            {/* Right section: dates and periods */}
            <div className="apv-form-section">
              <div className="apv-form-section-title">
                <FontAwesomeIcon icon={faCalendarAlt} className="text-primary" /> Contract Period
              </div>

              <div className="row">
                <div className="col-md-6 apv-form-group">
                  <label>Start Date <span className="apv-required-star">*</span></label>
                  <input 
                    type="date"
                    value={startDate}
                    onChange={(e) => {
                      setStartDate(e.target.value);
                      clearError('startDate');
                      clearError('endDate');
                    }}
                    className={errors.startDate ? 'invalid' : ''}
                  />
                  {errors.startDate && <span className="apv-error-message">{errors.startDate}</span>}
                </div>
                <div className="col-md-6 apv-form-group">
                  <label>End Date <span className="apv-required-star">*</span></label>
                  <input 
                    type="date"
                    value={endDate}
                    onChange={(e) => {
                      setEndDate(e.target.value);
                      clearError('endDate');
                    }}
                    className={errors.endDate ? 'invalid' : ''}
                  />
                  {errors.endDate && <span className="apv-error-message">{errors.endDate}</span>}
                </div>
              </div>

              <div className="apv-form-group">
                <label>Total Duration (Calculated)</label>
                <div className="apv-calculated-banner">
                  {duration.totalWeeks > 0 || duration.totalMonths > 0 || duration.months > 0 || duration.weeks > 0
                    ? formatPeriod(0, duration.totalMonths || duration.months, duration.weeks) 
                    : 'Please enter valid Start & End Dates'}
                </div>
              </div>
            </div>

            {/* Bottom section 1: Base and Option contract values */}
            <div className="apv-form-section">
              <div className="apv-form-section-title">
                <FontAwesomeIcon icon={faDollarSign} className="text-primary" /> Base Contract Information
              </div>

              <div className="apv-form-group">
                <label>Base Contract Period <span className="apv-required-star">*</span></label>
                <div className="apv-period-row">
                  <div className="apv-period-col">
                    <div className="apv-period-input-group">
                      <input 
                        type="number"
                        min="0"
                        max={duration.totalMonths || 99}
                        placeholder="0"
                        value={basePeriodMonths}
                        onChange={(e) => {
                          setBasePeriodMonths(e.target.value);
                          clearError('basePeriod');
                        }}
                        className={errors.basePeriod ? 'invalid' : ''}
                      />
                      <span className="apv-period-addon">Months</span>
                    </div>
                  </div>
                  <div className="apv-period-col">
                    <div className="apv-period-input-group">
                      <input 
                        type="number"
                        min="0"
                        max="4"
                        placeholder="0"
                        value={basePeriodWeeks}
                        onChange={(e) => {
                          setBasePeriodWeeks(e.target.value);
                          clearError('basePeriod');
                        }}
                        className={errors.basePeriod ? 'invalid' : ''}
                      />
                      <span className="apv-period-addon">Weeks</span>
                    </div>
                  </div>
                </div>
                <small style={{ color: '#6c757d', marginTop: '4px' }}>
                  {duration.totalWeeks > 0 || duration.totalMonths > 0 || duration.months > 0 || duration.weeks > 0
                    ? `Available: ${formatPeriod(0, duration.totalMonths || duration.months, duration.weeks)} total (auto-calculated)`
                    : 'Please enter Start & End Dates first'}
                </small>
                {errors.basePeriod && <span className="apv-error-message">{errors.basePeriod}</span>}
              </div>

              <div className="apv-form-group">
                <label>Base Contract Amount ($) <span className="apv-required-star">*</span></label>
                <input 
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="Enter base amount in USD"
                  value={baseAmount}
                  onChange={(e) => {
                    setBaseAmount(e.target.value);
                    clearError('baseAmount');
                  }}
                  className={errors.baseAmount ? 'invalid' : ''}
                />
                {errors.baseAmount && <span className="apv-error-message">{errors.baseAmount}</span>}
              </div>
            </div>

            <div className="apv-form-section">
              <div className="apv-form-section-title">
                <FontAwesomeIcon icon={faDollarSign} className="text-primary" /> Option Period Information
              </div>

              <div className="apv-form-group">
                <label>Option Period (Calculated)</label>
                <div className="apv-calculated-banner" style={{ backgroundColor: '#e2f0d9', color: '#385723' }}>
                  {optionPeriod.totalWeeks > 0 || optionPeriod.months > 0 || optionPeriod.weeks > 0
                    ? formatPeriod(0, optionPeriod.months, optionPeriod.weeks) 
                    : '0 week'}
                </div>
                <small style={{ color: '#6c757d', marginTop: '3px' }}>
                  Remaining duration after base contract period (auto-deducted)
                </small>
              </div>

              <div className="apv-form-group">
                <label>Option Amount ($) {hasOptionPeriod && <span className="apv-required-star">*</span>}</label>
                <input 
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="Enter option amount in USD"
                  value={optionAmount}
                  onChange={(e) => {
                    setOptionAmount(e.target.value);
                    clearError('optionAmount');
                  }}
                  className={errors.optionAmount ? 'invalid' : ''}
                  // disabled={optionPeriod.totalMonths <= 0}
                />
                {errors.optionAmount && <span className="apv-error-message">{errors.optionAmount}</span>}
              </div>
            </div>

            {/* Bottom section 2: Contingency calculation and APV display */}
            <div className="apv-form-section">
              <div className="apv-form-section-title">
                <FontAwesomeIcon icon={faDollarSign} className="text-primary" /> Contingency Calculation
              </div>

              <div className="apv-form-group">
                <label>Contingency Percentage (%)</label>
                <input 
                  type="number"
                  step="0.1"
                  min="0"
                  max="100"
                  value={contingencyPercentage}
                  onChange={(e) => setContingencyPercentage(e.target.value)}
                />
                <small style={{ color: '#6c757d', marginTop: '3px' }}>
                  5% set as default, user can edit
                </small>
              </div>

              <div className="apv-form-group">
                <label>Contingency Amount (Calculated)</label>
                <input 
                  type="text"
                  value={formatValue(contingencyAmount)}
                  readOnly
                />
                <small style={{ color: '#6c757d', marginTop: '3px' }}>
                  Auto-calculated: (Base Amount + Option Amount) * Percentage
                </small>
              </div>
            </div>

            <div className="apv-form-section">
              <div className="apv-form-section-title">
                <FontAwesomeIcon icon={faDollarSign} className="text-primary" /> Total APV (Approved Project Value)
              </div>
              
              <div className="apv-summary-display">
                <div className="apv-summary-row base-option">
                  <span>Base + Option</span>
                  <span>{formatValue(basePlusOption)}</span>
                </div>
                <div className="apv-summary-row contingency">
                  <span>+ Contingency ({contingencyPercentage}%)</span>
                  <span>{formatValue(contingencyAmount)}</span>
                </div>
                <div className="apv-summary-row">
                  <span>Total APV</span>
                  <span>{formatValue(totalApvValue)}</span>
                </div>
              </div>
            </div>

          </div>

          <div className="apv-form-group">
            <label>Remarks</label>
            <textarea 
              name="remarks" 
              rows="3" 
              placeholder="Add any remarks or descriptions here..."
              defaultValue={initialData ? initialData.remarks : ''}
            ></textarea>
          </div>

          <div className="apv-form-actions">
            <button 
              type="submit" 
              className={`apv-btn ${initialData ? 'success' : 'primary'}`}
              disabled={isSaving}
            >
              {isSaving 
                ? (initialData ? 'Updating...' : 'Saving...') 
                : (initialData ? 'Update Contract Detail' : 'Save Contract Details')}
            </button>
            <button type="button" className="apv-btn secondary" onClick={onCancel} disabled={isSaving}>Cancel</button>
          </div>

        </form>
      </div>
    </div>
  );
}

export default ContractForm;
